import { useState, useCallback } from 'react';
import type { MoodboardItem } from '../../../types/topic';
import { supabase } from '../../../lib/supabase';
import { validateImageFile } from '../../../utils/imageValidation';
import { logger } from '../../../utils/logger';

interface UseImageProcessingProps {
  topicId: string;
  onImagesProcessed: (images: MoodboardItem[]) => Promise<void>;
}

interface UploadStats {
  total: number;
  completed: number;
  failed: number;
}

export function useImageProcessing({ topicId, onImagesProcessed }: UseImageProcessingProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadStats, setUploadStats] = useState<UploadStats>({
    total: 0,
    completed: 0,
    failed: 0,
  });

  const processImages = useCallback(
    async (files: File[]) => {
      setError(null);
      setIsUploading(true);
      setUploadStats({
        total: files.length,
        completed: 0,
        failed: 0,
      });

      try {
        const newImages: MoodboardItem[] = [];

        for (const file of files) {
          // Validate file type and size
          const validation = validateImageFile(file);
          if (!validation.valid) {
            logger.warn(`Skipping file ${file.name}: ${validation.error}`);
            setUploadStats(prev => ({
              ...prev,
              failed: prev.failed + 1,
            }));
            // Set error message for user feedback (only first error shown)
            if (!error) {
              setError(validation.error || 'Invalid file type');
            }
            continue;
          }

          try {
            // Create a temporary URL for size calculation
            const url = URL.createObjectURL(file);
            const img = new Image();

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
              img.src = url;
            });

            // Resize if needed
            let width = img.width;
            let height = img.height;

            if (width > 1200 || height > 1200) {
              const ratio = Math.min(1200 / width, 1200 / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }

            // Create canvas for WebP conversion
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Could not get canvas context');

            ctx.drawImage(img, 0, 0, width, height);
            URL.revokeObjectURL(url);

            // Convert to WebP
            const webpBlob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                blob => {
                  if (blob) resolve(blob);
                  else reject(new Error('Failed to convert image'));
                },
                'image/webp',
                0.9
              );
            });

            // Upload to Supabase storage
            const fileName = `${topicId}/${Date.now()}-${file.name.replace(/\.[^/.]+$/, '')}.webp`;

            // Get current auth info for logging
            const {
              data: { user },
            } = await supabase.auth.getUser();
            logger.fetch(`Uploading image as user: ${user?.id} to topic: ${topicId}`);

            const { error: uploadError } = await supabase.storage
              .from('topic-images')
              .upload(fileName, webpBlob, {
                contentType: 'image/webp',
                upsert: false,
              });

            if (uploadError) {
              logger.error('Storage upload error:', {
                error: uploadError,
                code: uploadError.code,
                message: uploadError.message,
                details: uploadError.details,
                hint: uploadError.hint,
              });
              throw uploadError;
            }

            logger.fetch('File uploaded successfully:', fileName);

            const {
              data: { publicUrl },
            } = supabase.storage.from('topic-images').getPublicUrl(fileName);

            newImages.push({
              topic_id: topicId,
              id: crypto.randomUUID(),
              url: publicUrl,
              width,
              height,
              created_at: new Date().toISOString(),
            });

            setUploadStats(prev => ({
              ...prev,
              completed: prev.completed + 1,
            }));
          } catch (err) {
            logger.error('Error processing image:', err);
            setUploadStats(prev => ({
              ...prev,
              failed: prev.failed + 1,
            }));
          }
        }

        if (newImages.length > 0) {
          await onImagesProcessed(newImages);
        }
      } catch (err) {
        logger.error('Error uploading images:', err);

        // Provide more specific error message based on error type
        if (err instanceof Error) {
          if (err.message.includes('security policy')) {
            setError('Permission denied. You may not have access to upload to this folder.');
          } else if (err.message.includes('authentication')) {
            setError('Authentication error. Please log in again.');
          } else {
            setError(`Upload failed: ${err.message}`);
          }
        } else {
          setError('Failed to upload images. Please try again.');
        }

        // Try to log storage permissions
        try {
          const { data, error } = await supabase.rpc('get_my_claims');
          logger.debug('User claims:', data, error);
        } catch (claimsErr) {
          logger.error('Error checking claims:', claimsErr);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [topicId, onImagesProcessed]
  );

  return {
    isUploading,
    error,
    uploadStats,
    processImages,
    setUploadStats,
    setError,
  };
}
